import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '../../home/components/Header';
import Footer from '../../home/components/Footer';
import BlogRelatedStrip from '../../../components/feature/BlogRelatedStrip';
import BlogProgressBar from '../../../components/feature/BlogProgressBar';

const SITE_URL = 'https://hoosierlifeinsurance.com';
const CANONICAL = `${SITE_URL}/blog/no-medical-exam-life-insurance-indiana`;
const OG_IMAGE = 'https://public.readdy.ai/ai/img_res/3c319ee8e1a87eb3575c4b2886787fd6.jpg';

export default function NoMedicalExamLifeInsuranceIndiana() {
  useEffect(() => {
    window.scrollTo(0, 0);

    // Set canonical URL
    let canonicalEl = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!canonicalEl) {
      canonicalEl = document.createElement('link');
      canonicalEl.rel = 'canonical';
      document.head.appendChild(canonicalEl);
    }
    canonicalEl.href = CANONICAL;

    document.title = 'No Medical Exam Life Insurance Indiana: Get Covered Today (2025)';

    const schemas = [
      {
        '@context': 'https://schema.org',
        '@type': 'Article',
        headline: 'No Medical Exam Life Insurance Indiana: Get Covered Without a Doctor Visit (2025)',
        description: 'Compare no-exam life insurance options in Indiana for 2025. Simplified issue vs. guaranteed issue rates, top carriers, coverage limits, and how to get approved same-day.',
        author: {
          '@type': 'Person',
          '@id': 'https://hoosierlifeinsurance.com/about#agent',
          name: 'Benjamin Wright',
          jobTitle: 'Licensed Life Insurance Agent',
          url: 'https://hoosierlifeinsurance.com/about',
          worksFor: { '@type': 'InsuranceAgency', name: 'Hoosier Life Insurance', url: SITE_URL },
          knowsAbout: ['No Medical Exam Life Insurance', 'Simplified Issue Life Insurance', 'Indiana Life Insurance', 'Guaranteed Issue Life Insurance'],
        },
        publisher: { '@type': 'Organization', name: 'Hoosier Life Insurance', url: SITE_URL },
        datePublished: '2025-03-10T00:00:00Z',
        dateModified: new Date().toISOString(),
        url: CANONICAL,
        mainEntityOfPage: 'https://hoosierlifeinsurance.com/blog/no-medical-exam-life-insurance-indiana',
        image: { '@type': 'ImageObject', url: 'https://readdy.ai/api/search-image?query=relaxed%20Indiana%20man%20in%20his%2040s%20completing%20life%20insurance%20application%20on%20laptop%20at%20bright%20home%20office%20clean%20white%20minimal%20background%20no%20stress%20easy%20approval%20instant%20coverage%20Indiana%20warm%20natural%20light&width=1200&height=630&seq=blog-noexam-og&orientation=landscape', width: 1200, height: 630 },
        timeRequired: 'PT11M',
        wordCount: 2200,
        inLanguage: 'en-US',
        isPartOf: {
          '@type': 'WebSite',
          url: SITE_URL
        },
        speakable: {
          '@type': 'SpeakableSpecification',
          cssSelector: ['h1', 'h2', 'details summary']
        },
      },
      {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: [
          {
            '@type': 'Question',
            name: 'Can I get life insurance in Indiana without a medical exam?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'Yes. Simplified issue policies approve you based on health questions only — no blood draw, no urine test. Guaranteed issue policies ask zero health questions and approve anyone aged 50–85. Coverage amounts run from $5,000 to $500,000 depending on the policy type.',
            },
          },
          {
            '@type': 'Question',
            name: 'Is no-exam life insurance more expensive in Indiana?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'Typically 20–40% more than a fully underwritten policy for the same coverage. A healthy 35-year-old might pay $28/month for $500K with a full exam but $38–$45/month for simplified issue. For many people the speed and convenience is worth that difference — especially if you have minor health issues that could complicate underwriting.',
            },
          },
          {
            '@type': 'Question',
            name: 'What is the maximum coverage I can get without a medical exam in Indiana?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'Simplified issue term life can go up to $500,000 from carriers like Bestow and Ladder. Most simplified whole life policies cap at $150,000–$250,000. Guaranteed issue final expense policies typically max out at $25,000–$35,000.',
            },
          },
          {
            '@type': 'Question',
            name: 'How quickly is no-exam life insurance approved in Indiana?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'Most simplified issue policies give a decision in 5–20 minutes online. Some carriers like Ethos and Bestow offer same-day coverage effective immediately upon payment. Guaranteed issue policies are approved instantly with no questions asked.',
            },
          },
        ],
      },
    ];

    schemas.forEach((schema, idx) => {
      const el = document.createElement('script');
      el.type = 'application/ld+json';
      el.id = `schema-noexam-${idx}`;
      el.textContent = JSON.stringify(schema);
      document.head.appendChild(el);
    });

    return () => {
      schemas.forEach((_, idx) => document.getElementById(`schema-noexam-${idx}`)?.remove());
    };
  }, []);

  const carrierTable = [
    { carrier: 'Bestow', type: 'Simplified Issue Term', coverage: 'Up to $1.5M', age: '18–60', exam: 'None', decision: '5 min', rating: 'A' },
    { carrier: 'Ladder', type: 'Simplified Issue Term', coverage: 'Up to $3M', age: '20–60', exam: 'None', decision: '5 min', rating: 'A+' },
    { carrier: 'Ethos', type: 'Simplified Issue Term', coverage: 'Up to $2M', age: '20–65', exam: 'None', decision: '10 min', rating: 'A' },
    { carrier: 'Mutual of Omaha', type: 'Simplified Issue Whole', coverage: 'Up to $300K', age: '45–85', exam: 'None', decision: '24 hrs', rating: 'A+' },
    { carrier: 'Transamerica', type: 'Simplified Issue Whole', coverage: 'Up to $249K', age: '18–80', exam: 'None', decision: '24 hrs', rating: 'A' },
    { carrier: 'Gerber Life', type: 'Guaranteed Issue Whole', coverage: '$5K–$25K', age: '50–80', exam: 'None', decision: 'Instant', rating: 'A' },
    { carrier: 'AIG/American General', type: 'Guaranteed Issue Whole', coverage: '$5K–$25K', age: '50–85', exam: 'None', decision: 'Instant', rating: 'A' },
  ];

  const rateComparison = [
    { age: '30', gender: 'Male', coverage: '$500K / 20yr', exam: '$22/mo', noExam: '$34/mo', diff: '+55%' },
    { age: '35', gender: 'Female', coverage: '$500K / 20yr', exam: '$18/mo', noExam: '$27/mo', diff: '+50%' },
    { age: '40', gender: 'Male', coverage: '$500K / 20yr', exam: '$38/mo', noExam: '$55/mo', diff: '+45%' },
    { age: '45', gender: 'Female', coverage: '$500K / 20yr', exam: '$42/mo', noExam: '$60/mo', diff: '+43%' },
    { age: '50', gender: 'Male', coverage: '$250K / 20yr', exam: '$62/mo', noExam: '$88/mo', diff: '+42%' },
    { age: '55', gender: 'Female', coverage: '$150K whole life', exam: '$110/mo', noExam: '$148/mo', diff: '+35%' },
  ];

  const whoShouldConsider = [
    { icon: 'ri-alarm-line', title: 'You need coverage fast', desc: 'Pending adoption, new mortgage closing, or business deal that requires proof of coverage — same-day approval is possible.' },
    { icon: 'ri-heart-add-line', title: 'Minor health conditions', desc: 'Controlled high blood pressure, pre-diabetes, or past skin cancer (not melanoma) — conditions that complicate full underwriting but don\'t disqualify you from simplified issue.' },
    { icon: 'ri-dislike-line', title: 'You have a needle phobia', desc: 'Genuinely common. Simplified issue is a real solution, not just a premium product.' },
    { icon: 'ri-user-heart-line', title: 'Seniors aged 65+', desc: 'Full underwriting gets harder and more invasive after 65. A guaranteed issue final expense policy provides dignified, hassle-free coverage with no questions.' },
    { icon: 'ri-time-line', title: 'Employer coverage ending', desc: 'You have 30 days to replace group life when you leave a job. Simplified issue can bridge the gap immediately without waiting 4–6 weeks for full underwriting.' },
  ];

  return (
    <>
      <title>No Medical Exam Life Insurance Indiana: Get Covered Today (2026) | Hoosier Life</title>
      <meta name="description" content="Compare no medical exam life insurance in Indiana. Simplified issue up to $500K, guaranteed issue for seniors, same-day approval. Licensed agent." />
      <meta name="keywords" content="no medical exam life insurance Indiana, no exam life insurance Indiana, instant approval life insurance Indiana, simplified issue life insurance Indiana" />
      <meta property="og:title" content="No Medical Exam Life Insurance Indiana: Get Covered Today (2025)" />
      <meta property="og:description" content="No blood tests, no doctor visits. Get Indiana life insurance approved in minutes with simplified or guaranteed issue policies. Real 2025 rates compared." />
      <meta property="og:url" content={CANONICAL} />
      <link rel="canonical" href={CANONICAL} />
      <meta name="robots" content="index, follow" />
      <meta property="og:type" content="article" />
      <meta property="og:image" content={OG_IMAGE} />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:image" content={OG_IMAGE} />

      <BlogProgressBar />
      <Header />
      <article className="min-h-screen bg-white">

        {/* Hero */}
        <div className="bg-gradient-to-br from-[#1A3A52] to-[#0F2A3F] text-white pt-28 pb-16">
          <div className="max-w-4xl mx-auto px-6">
            <Link to="/blog" className="inline-flex items-center text-white/60 hover:text-[#D97642] mb-6 transition-colors text-sm cursor-pointer">
              <i className="ri-arrow-left-line mr-2"></i>Back to Blog
            </Link>
            <div className="flex items-center gap-3 mb-4">
              <span className="bg-emerald-500/20 text-emerald-300 text-xs font-bold px-3 py-1.5 rounded-full">No Exam</span>
              <span className="text-white/50 text-xs">11 min read · March 2025</span>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold mb-5 leading-tight">No Medical Exam Life Insurance in Indiana: Get Covered Without a Doctor Visit</h1>
            <p className="text-white/75 text-xl leading-relaxed max-w-2xl">No blood draw. No urine test. No waiting weeks for results. Here's exactly how to get quality life insurance coverage in Indiana today.</p>
            <div className="flex flex-wrap gap-4 mt-8">
              <Link to="/instant-quote" className="px-6 py-3 bg-[#D97642] text-white rounded-lg font-bold hover:bg-[#C26638] transition-colors whitespace-nowrap cursor-pointer">Get No-Exam Quote</Link>
              <Link to="/book-appointment" className="px-6 py-3 bg-white/10 text-white rounded-lg font-semibold border border-white/20 hover:bg-white/20 transition-colors whitespace-nowrap cursor-pointer">Talk to an Agent</Link>
            </div>
          </div>
        </div>

        <div className="max-w-4xl mx-auto px-6 py-12">

          {/* Quick Answer Box */}
          <div className="bg-emerald-50 border-l-4 border-emerald-500 p-6 rounded-r-xl mb-10">
            <p className="font-bold text-gray-900 mb-2 flex items-center gap-2"><i className="ri-checkbox-circle-line text-emerald-500 text-lg"></i>Bottom Line Up Front</p>
            <p className="text-gray-700 leading-relaxed text-[15px]">
              Yes — you can get up to <strong>$2,000,000 in Indiana life insurance coverage</strong> today without a medical exam. Simplified issue policies use health questions only (no needles) and approve most applicants in under 10 minutes. Costs run 35–55% more than fully underwritten policies, but for many Hoosiers the speed, convenience, or health situation makes it the right call.
            </p>
          </div>

          {/* Key Takeaways */}
          <div className="bg-[#1A3A52] text-white rounded-2xl p-7 mb-10">
            <h2 className="text-lg font-bold mb-4 flex items-center gap-2">
              <i className="ri-bookmark-3-fill text-[#D97642] text-xl"></i>
              Key Takeaways
            </h2>
            <ul className="space-y-2.5">
              {[
                'Simplified issue life insurance approves up to $2M+ in coverage with health questions only — no blood draw, no urine test, no medical exam required',
                'Guaranteed issue accepts everyone aged 50–85 with zero health questions — but includes a 2-year graded benefit period and costs 20–35% more',
                'No-exam life insurance costs 35–55% more than fully underwritten policies — a healthy 35-year-old pays $28/month with full exam vs. $38–$45/month simplified issue',
                'Same-day coverage is available from carriers like Bestow and Ethos — most simplified issue decisions come back in 5–20 minutes online',
                'Full medical exam policies remain the better choice for healthy applicants who want the lowest possible rate and can wait 4–6 weeks for coverage',
              ].map((point, i) => (
                <li key={i} className="flex items-start gap-3 text-sm text-white/90">
                  <i className="ri-check-line text-[#D97642] mt-0.5 flex-shrink-0"></i>
                  {point}
                </li>
              ))}
            </ul>
          </div>

          {/* Intro */}
          <p className="text-gray-700 text-lg leading-relaxed mb-4">
            The traditional life insurance process takes 4–8 weeks: paramedical exam, blood draw, urine sample, attending physician statements, and underwriting review. For millions of Americans, that process is a dealbreaker — whether because of a pressing timeline, mild health issues, needle anxiety, or just not wanting a stranger at their kitchen table with a phlebotomy kit.
          </p>
          <p className="text-gray-700 text-lg leading-relaxed mb-10">
            No-exam life insurance has matured significantly. In 2025, you can get genuine, substantive coverage through legitimate carriers — not just burial-cost policies — without setting foot in a doctor's office. This guide breaks down exactly what's available to Indiana residents, what it costs, and when it makes sense.
          </p>

          {/* The two types */}
          <section className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-3">The Two Types of No-Exam Life Insurance in Indiana</h2>
            <div className="bg-gray-50 border-l-4 border-[#D97642] p-4 rounded-r-lg mb-5 text-gray-800 text-sm leading-relaxed">
              <strong>Direct answer:</strong> Simplified issue uses health questions (no exam) — coverage up to $2M+, decisions in 5–15 minutes, 35–50% more expensive than fully underwritten. Guaranteed issue has zero health questions — coverage up to $35K for ages 50–85, instant approval, but 20–35% more expensive than simplified issue and includes a 2-year graded benefit period. Always try simplified issue first.
            </div>
            <p className="text-gray-600 mb-6">Not all "no exam" policies are the same. Understanding the difference saves you money and avoids surprises at claim time.</p>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-[#1A3A52]/5 border border-[#1A3A52]/15 rounded-2xl p-6">
                <div className="w-12 h-12 bg-[#1A3A52] rounded-xl flex items-center justify-center mb-4">
                  <i className="ri-questionnaire-line text-white text-xl"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Simplified Issue</h3>
                <p className="text-gray-600 text-sm leading-relaxed mb-4">You answer a short health questionnaire (10–20 questions). No exam. Underwriting is automated — most decisions come back in 5–15 minutes. Coverage up to <strong>$2M+</strong> from top carriers.</p>
                <ul className="space-y-2 text-sm">
                  {['No blood draw or urine test','Health questions required','May be declined for serious conditions','Best rates of the no-exam options','Coverage from $50K to $2M+','Available from age 18–65'].map(i => (
                    <li key={i} className="flex items-center gap-2 text-gray-700"><i className="ri-check-line text-emerald-500 flex-shrink-0"></i>{i}</li>
                  ))}
                </ul>
                <div className="mt-4 bg-[#1A3A52]/10 rounded-lg px-4 py-2 text-xs font-semibold text-[#1A3A52]">Best for: Healthy to moderately healthy adults who want speed</div>
              </div>
              <div className="bg-[#D97642]/5 border border-[#D97642]/15 rounded-2xl p-6">
                <div className="w-12 h-12 bg-[#D97642] rounded-xl flex items-center justify-center mb-4">
                  <i className="ri-shield-check-line text-white text-xl"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Guaranteed Issue</h3>
                <p className="text-gray-600 text-sm leading-relaxed mb-4">Zero health questions. Zero medical exam. You cannot be turned down based on health. Coverage is smaller (typically $5K–$35K) and premiums are higher per dollar of coverage.</p>
                <ul className="space-y-2 text-sm">
                  {['Zero health questions','Cannot be declined for health reasons','2-year graded benefit period','Higher cost per $1,000 of coverage','Coverage from $5K to $35K','Available from age 50–85'].map(i => (
                    <li key={i} className="flex items-center gap-2 text-gray-700"><i className="ri-check-line text-[#D97642] flex-shrink-0"></i>{i}</li>
                  ))}
                </ul>
                <div className="mt-4 bg-[#D97642]/10 rounded-lg px-4 py-2 text-xs font-semibold text-[#D97642]">Best for: Seniors with significant health issues needing final expense coverage</div>
              </div>
            </div>

            {/* Graded Benefit Warning */}
            <div className="mt-6 bg-amber-50 border-l-4 border-amber-400 p-5 rounded-r-xl">
              <p className="font-bold text-amber-800 flex items-center gap-2 mb-1"><i className="ri-alert-line"></i>Important: Graded Benefit Periods</p>
              <p className="text-amber-700 text-sm leading-relaxed">Most guaranteed issue and some simplified issue policies have a <strong>2-year graded benefit period</strong>. If you pass away in the first 2 years from natural causes, your beneficiaries receive only a return of premiums paid (plus interest) — not the full death benefit. After year 2, full benefits are paid for any cause of death. Accidental death is typically covered in full from day one.</p>
            </div>
          </section>

          {/* Who should consider */}
          <section className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-3">Who Should Consider No-Exam Life Insurance in Indiana?</h2>
            <p className="text-gray-600 mb-6">No-exam coverage isn't just for people who "can't pass" a medical exam. There are five legitimate reasons Indiana residents choose this path:</p>
            <div className="space-y-4">
              {whoShouldConsider.map((item, i) => (
                <div key={i} className="flex items-start gap-5 bg-white border border-gray-100 rounded-xl p-5 hover:border-[#D97642]/30 transition-colors">
                  <div className="w-12 h-12 bg-[#D97642]/10 rounded-xl flex items-center justify-center flex-shrink-0">
                    <i className={`${item.icon} text-[#D97642] text-xl`}></i>
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900 mb-1">{item.title}</h3>
                    <p className="text-gray-600 text-sm leading-relaxed">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Rate comparison table */}
          <section className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-3">No-Exam vs. Full Underwriting: Real Indiana Rate Comparison (2025)</h2>
            <div className="bg-gray-50 border-l-4 border-[#D97642] p-4 rounded-r-lg mb-5 text-gray-800 text-sm leading-relaxed">
              <strong>Direct answer:</strong> A 35-year-old Indiana female pays $18/month for $500K/20yr term with a full exam, vs. $27/month simplified issue — a $108/year difference for the same coverage. For a 50-year-old male, the gap is $62/month with exam vs. $88/month no-exam — $312/year more for convenience. Decide whether the time savings and convenience is worth that annual premium difference.
            </div>
            <p className="text-gray-600 mb-6">The same coverage amount, side-by-side. Healthy, non-smoking Indiana residents. This is what convenience costs you:</p>
            <div className="overflow-x-auto rounded-xl border border-gray-200">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-[#1A3A52] text-white">
                    {['Age', 'Gender', 'Policy', 'With Full Exam', 'No-Exam Rate', 'Premium Diff'].map(h => (
                      <th key={h} className="px-4 py-3 text-left font-semibold">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {rateComparison.map((r, i) => (
                    <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                      <td className="px-4 py-3 font-medium">{r.age}</td>
                      <td className="px-4 py-3 text-gray-600">{r.gender}</td>
                      <td className="px-4 py-3 text-gray-700">{r.coverage}</td>
                      <td className="px-4 py-3 text-emerald-700 font-semibold">{r.exam}</td>
                      <td className="px-4 py-3 text-[#D97642] font-semibold">{r.noExam}</td>
                      <td className="px-4 py-3 text-amber-600 font-bold">{r.diff}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-xs text-gray-500 mt-2">*2025 estimated rates from top-rated carriers. Actual rates vary by carrier and individual health profile.</p>
          </section>

          {/* Carrier comparison */}
          <section className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-3">Best No-Exam Life Insurance Carriers in Indiana (2025)</h2>
            <p className="text-gray-600 mb-6">These carriers are actively writing no-exam policies for Indiana residents in 2025, with strong AM Best ratings and competitive rates:</p>
            <div className="overflow-x-auto rounded-xl border border-gray-200">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-[#1A3A52] text-white">
                    {['Carrier', 'Policy Type', 'Max Coverage', 'Ages', 'Exam Required', 'Decision Speed', 'AM Best'].map(h => (
                      <th key={h} className="px-4 py-3 text-left font-semibold whitespace-nowrap">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {carrierTable.map((r, i) => (
                    <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                      <td className="px-4 py-3 font-bold text-[#1A3A52]">{r.carrier}</td>
                      <td className="px-4 py-3 text-gray-700">{r.type}</td>
                      <td className="px-4 py-3 font-semibold text-[#D97642]">{r.coverage}</td>
                      <td className="px-4 py-3 text-gray-600">{r.age}</td>
                      <td className="px-4 py-3">
                        <span className="inline-flex items-center gap-1 bg-emerald-50 text-emerald-700 text-xs font-semibold px-2 py-1 rounded-full">
                          <i className="ri-check-line"></i>{r.exam}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-gray-700 font-medium">{r.decision}</td>
                      <td className="px-4 py-3 font-bold text-emerald-700">{r.rating}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          {/* How to apply */}
          <section className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">How to Apply for No-Exam Life Insurance in Indiana — Step by Step</h2>
            <div className="space-y-4">
              {[
                { step: '01', title: 'Determine your coverage need', body: 'Use the DIME method (Debt + Income × years + Mortgage + Education). Most Indiana families need $500K–$1.5M. Don\'t underinsure just because no-exam is faster.' },
                { step: '02', title: 'Choose your policy type', body: 'Healthy and under 60? Simplified issue term gives you the most coverage at the best price. Senior with health issues? Guaranteed issue whole life covers final expenses with zero questions.' },
                { step: '03', title: 'Compare at least 3 carriers', body: 'No-exam rates vary significantly by carrier. A 40-year-old male might get $500K for $52/month from one carrier and $67/month from another. We shop 10+ carriers to find the best rate.' },
                { step: '04', title: 'Answer the health questionnaire honestly', body: 'Simplified issue applications ask 10–20 questions about your health history. Answer truthfully — misrepresentation voids the policy. If you\'re in good health, you\'ll likely get a decision in 5–15 minutes.' },
                { step: '05', title: 'Review and accept coverage', body: 'Once approved, review the policy terms including the graded benefit period (if any). Most carriers offer a 30-day free look period — you can cancel for a full refund if you change your mind.' },
              ].map((s, i) => (
                <div key={i} className="flex items-start gap-5 bg-gray-50 rounded-xl p-5">
                  <div className="w-12 h-12 bg-[#D97642] text-white rounded-xl flex items-center justify-center flex-shrink-0 text-lg font-black">{s.step}</div>
                  <div>
                    <h3 className="font-bold text-gray-900 mb-1">{s.title}</h3>
                    <p className="text-gray-600 text-sm leading-relaxed">{s.body}</p>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Related Blog Links */}
          <div className="bg-gray-50 rounded-2xl p-7 mb-10">
            <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
              <i className="ri-links-line text-[#D97642]"></i>
              Related Reading
            </h3>
            <div className="grid sm:grid-cols-2 gap-4">
              <Link to="/blog/term-life-insurance-indianapolis" className="flex items-start gap-3 bg-white rounded-xl p-4 border border-gray-200 hover:border-[#D97642]/40 transition-colors group">
                <div className="w-10 h-10 bg-[#1A3A52]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                  <i className="ri-file-list-3-line text-[#1A3A52]"></i>
                </div>
                <div>
                  <span className="font-bold text-gray-900 text-sm group-hover:text-[#D97642] transition-colors block">Term Life Insurance Guide</span>
                  <p className="text-xs text-gray-500 mt-1">Compare no-exam vs. fully underwritten term policies</p>
                </div>
              </Link>
              <Link to="/blog/how-much-life-insurance-indiana" className="flex items-start gap-3 bg-white rounded-xl p-4 border border-gray-200 hover:border-[#D97642]/40 transition-colors group">
                <div className="w-10 h-10 bg-[#1A3A52]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                  <i className="ri-calculator-line text-[#1A3A52]"></i>
                </div>
                <div>
                  <span className="font-bold text-gray-900 text-sm group-hover:text-[#D97642] transition-colors block">How Much Life Insurance Do I Need?</span>
                  <p className="text-xs text-gray-500 mt-1">Calculate your coverage needs before choosing a policy type</p>
                </div>
              </Link>
            </div>
          </div>

          {/* When full underwriting is worth it */}
          <section className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">When a Full Medical Exam Is Worth It</h2>
            <p className="text-gray-600 mb-5">For all its convenience, no-exam insurance isn't always the right choice. Consider the traditional exam-based route if:</p>
            <div className="grid md:grid-cols-2 gap-4">
              {[
                { icon: 'ri-scales-3-line', color: 'text-[#1A3A52]', title: 'You need more than $1.5M', body: 'Above certain thresholds, most carriers require full underwriting regardless. For large estates or business coverage, a medical exam unlocks better pricing and higher limits.' },
                { icon: 'ri-heart-pulse-line', color: 'text-emerald-600', title: 'You\'re in excellent health', body: 'If you\'re a healthy 30-year-old non-smoker, full underwriting gets you Preferred Plus rates — potentially 40–55% cheaper than simplified issue. Over a 20-year term, that\'s thousands of dollars.' },
                { icon: 'ri-time-line', color: 'text-[#D97642]', title: 'You have 4–6 weeks to spare', body: 'If your timeline allows, full underwriting delivers the best price for the best coverage. The exam itself takes about 30 minutes and is usually done at your home or office.' },
                { icon: 'ri-wallet-3-line', color: 'text-amber-600', title: 'You want the lowest possible premium', body: 'Budget matters. A 35-year-old paying $18/month vs. $27/month over 20 years is a $2,160 difference. Full underwriting wins on pure cost if you\'re healthy.' },
              ].map((c, i) => (
                <div key={i} className="bg-white border border-gray-100 rounded-xl p-5">
                  <div className="flex items-center gap-3 mb-2">
                    <i className={`${c.icon} ${c.color} text-xl`}></i>
                    <h3 className="font-bold text-gray-900 text-sm">{c.title}</h3>
                  </div>
                  <p className="text-gray-600 text-sm leading-relaxed">{c.body}</p>
                </div>
              ))}
            </div>
          </section>

          {/* Serving Your City */}
          <div className="bg-[#F9F7F4] rounded-2xl p-7 my-10">
            <h2 className="text-xl font-bold text-gray-900 mb-1">No-Exam Coverage Across Indiana</h2>
            <p className="text-sm text-gray-600 mb-5">Same-day approval is available statewide. Find local guidance for your area:</p>
            <div className="grid sm:grid-cols-2 gap-3">
              {[
                { to: '/life-insurance-fort-wayne', city: 'Fort Wayne', desc: 'Manufacturing & warehouse workers needing fast coverage' },
                { to: '/life-insurance-evansville', city: 'Evansville', desc: 'Toyota & ALCOA shift workers — no lengthy exam delays' },
                { to: '/life-insurance-south-bend', city: 'South Bend', desc: 'Notre Dame contract staff & gig-economy workers' },
                { to: '/fishers-life-insurance', city: 'Fishers', desc: 'Tech professionals with tight closing timelines' },
                { to: '/noblesville-life-insurance', city: 'Noblesville', desc: 'Hamilton County families replacing expiring group coverage' },
              ].map(({ to, city, desc }) => (
                <Link
                  key={to}
                  to={to}
                  className="flex items-center gap-3 bg-white rounded-xl p-4 border border-gray-100 hover:border-[#D97642]/40 transition-colors group cursor-pointer"
                >
                  <div className="w-8 h-8 flex items-center justify-center bg-emerald-500/10 rounded-lg flex-shrink-0">
                    <i className="ri-checkbox-circle-line text-emerald-600 text-sm"></i>
                  </div>
                  <div className="flex-1 min-w-0">
                    <span className="font-bold text-gray-900 text-sm group-hover:text-[#D97642] transition-colors">{city}</span>
                    <p className="text-xs text-gray-500 mt-0.5 truncate">{desc}</p>
                  </div>
                  <i className="ri-arrow-right-s-line text-gray-300 group-hover:text-[#D97642] transition-colors flex-shrink-0"></i>
                </Link>
              ))}
            </div>
          </div>

          {/* Author Bio */}
          <div className="border border-gray-200 rounded-2xl p-7 my-8 flex items-start gap-5">
            <div className="w-14 h-14 flex items-center justify-center bg-[#1A3A52] rounded-full flex-shrink-0">
              <i className="ri-shield-user-line text-white text-2xl"></i>
            </div>
            <div>
              <p className="font-bold text-gray-900 text-base mb-1">Written by the Licensed Life Insurance Specialists at Hoosier Life Insurance</p>
              <p className="text-sm text-gray-600 leading-relaxed mb-2">Our licensed Indiana agents help Hoosiers compare no-exam and fully underwritten life insurance options from Bestow, Ladder, Ethos, Mutual of Omaha, Transamerica, and other carriers to find the fastest approval at the best available rate.</p>
              <div className="flex flex-wrap gap-3 text-xs text-gray-500">
                <span className="flex items-center gap-1"><i className="ri-checkbox-circle-line text-[#D97642]"></i>Licensed in Indiana</span>
                <span className="flex items-center gap-1"><i className="ri-checkbox-circle-line text-[#D97642]"></i>Independent — 20+ Carriers</span>
                <span className="flex items-center gap-1"><i className="ri-checkbox-circle-line text-[#D97642]"></i>Last reviewed March 2025</span>
              </div>
            </div>
          </div>

          {/* FAQ Section */}
          <section className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Frequently Asked Questions</h2>
            <div className="space-y-4">
              {[
                { q: 'Can I get life insurance in Indiana without a medical exam?', a: 'Yes. Simplified issue policies approve you based on health questions only — no blood draw, no urine test. Guaranteed issue policies ask zero health questions and approve anyone aged 50–85. Coverage amounts run from $5,000 to $2,000,000 depending on the policy type and your age.' },
                { q: 'Is no-exam life insurance legitimate, or is it a lower quality product?', a: 'It\'s completely legitimate. The same major carriers offering fully underwritten policies — Mutual of Omaha, Transamerica, Prudential — also offer simplified issue products. The coverage is real, the claims are paid, and the policies are state-regulated. The only difference is the underwriting process.' },
                { q: 'What health conditions disqualify me from simplified issue?', a: 'Common disqualifiers include active cancer (within the last 5 years for most carriers), recent heart attack or stroke (within 2–3 years), HIV/AIDS, end-stage kidney disease, and active organ transplant status. Controlled conditions like managed hypertension, Type 2 diabetes, or past cancer (outside the exclusion window) typically qualify.' },
                { q: 'How quickly is no-exam life insurance approved in Indiana?', a: 'Most simplified issue policies give a decision in 5–20 minutes online. Some carriers like Ethos and Bestow offer same-day coverage effective immediately upon first payment. Guaranteed issue policies are approved instantly — no underwriting needed.' },
              ].map((item, i) => (
                <div key={i} className="border border-gray-200 rounded-xl p-5">
                  <h3 className="font-bold text-gray-900 mb-2 flex items-start gap-2">
                    <i className="ri-question-line text-[#D97642] mt-0.5 flex-shrink-0"></i>
                    {item.q}
                  </h3>
                  <p className="text-gray-600 text-sm leading-relaxed pl-6">{item.a}</p>
                </div>
              ))}
            </div>
          </section>

          {/* CTA */}
          <section className="bg-gradient-to-br from-[#1A3A52] to-[#0F2A3F] rounded-2xl p-8 text-center text-white mb-12">
            <div className="w-14 h-14 flex items-center justify-center bg-emerald-400/20 rounded-full mx-auto mb-4">
              <i className="ri-checkbox-circle-line text-emerald-400 text-2xl"></i>
            </div>
            <h2 className="text-2xl font-bold mb-3">Get Covered Today — No Exam Required</h2>
            <p className="text-white/70 mb-6 max-w-xl mx-auto">We'll shop Indiana's top no-exam carriers and have your best rate in minutes. No sales pressure, no commitment to apply.</p>
            <div className="flex flex-wrap gap-3 justify-center">
              <Link to="/instant-quote" className="px-6 py-3 bg-[#D97642] text-white rounded-lg font-bold hover:bg-[#C26638] transition-colors whitespace-nowrap cursor-pointer">Get No-Exam Quote Now</Link>
              <Link to="/book-appointment" className="px-6 py-3 bg-white/10 text-white rounded-lg font-bold hover:bg-white/20 border border-white/20 transition-colors whitespace-nowrap cursor-pointer">Book Free Consultation</Link>
            </div>
          </section>

        </div>
      </article>

      <BlogRelatedStrip currentSlug="no-medical-exam-life-insurance-indiana" />
      <Footer />
    </>
  );
}
